import {isFeatureEnabled} from '@github-ui/feature-flags'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {noop} from '@github-ui/noop'
import {Link, useLocation} from '@github-ui/react-router'
import type {SidebarLink} from '@github-ui/saved-views'
import {SidebarList} from '@github-ui/shared-dashboard-sidebar/SidebarList'
import {softNavigate} from '@github-ui/soft-navigate'
import {useAnalytics} from '@github-ui/use-analytics'
import {MilestoneIcon, StackIcon, TableIcon, TagIcon} from '@primer/octicons-react'
import {NavList, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import type {ComponentType, MouseEvent, ReactElement, ReactNode} from 'react'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {repoSidebarLinks} from './repos-issues-sidebar-links'
import {useIssuesSidebarState} from './use-issues-sidebar-state'
import {getRepoIssuesQueryPath} from './urls'
import styles from './ReposIssuesSidebar.module.css'
import {useFeedbackLink} from './CollapsibleFeedbackLink'

/** Shared analytics identifier for the repo-scoped issues sidebar. */
const DASHBOARD_TYPE = 'repo-issues'

export interface ReposIssuesSidebarProps {
  owner: string
  repo: string
  forceHardNavigation?: boolean
  /**
   * When true, renders the collapsible sidebar layout instead of the
   * legacy fixed-width sidebar. Only set when the component is wrapped
   * in a `<CollapsibleSidebar>` provider (flag ON path).
   */
  collapsible?: boolean
  /**
   * Whether the repository has the Projects feature enabled in its settings.
   * When false, the "Projects" link is hidden from the sidebar. Defaults to
   * false so the link is hidden if the server doesn't provide this value.
   */
  hasProjectsEnabled?: boolean
}

/**
 * Repo-scoped navigation sidebar for the Issues, Milestones and Labels pages.
 *
 * When `issues_react_collapsible_sidebar` is OFF, renders the legacy sidebar
 * that is visually identical to `main` (BetaLabel footer, original CSS).
 *
 * When the flag is ON, branches between an expanded tree (Primer NavList) and
 * a condensed icon rail. The collapse state is read from
 * `useCollapsibleSidebarContext()` which defaults to `isExpanded: true` when
 * no provider is present.
 */
export function ReposIssuesSidebar({
  owner,
  repo,
  forceHardNavigation = false,
  collapsible = false,
  hasProjectsEnabled = false,
}: ReposIssuesSidebarProps) {
  const isCollapsible = isFeatureEnabled('issues_react_collapsible_sidebar') && collapsible
  const {isExpanded} = useCollapsibleSidebarContext()
  const {pathname} = useLocation()
  const {selectedLink} = useIssuesSidebarState()
  const repoPath = `/${owner}/${repo}`
  const labelsPath = `${repoPath}/labels`
  const issuesPath = `${repoPath}/issues`
  const projectsPath = `${repoPath}/projects`
  const milestonesPath = `${repoPath}/milestones`
  const milestonePath = `${repoPath}/milestone/`
  const viewsPath = `${issuesPath}/views`

  // Track sidebar impressions so we can measure the ratio of users who
  // see the sidebar vs. those who click a link.
  const {sendAnalyticsEvent} = useAnalytics()
  const impressionSentRef = useRef(false)
  useEffect(() => {
    if (!impressionSentRef.current) {
      impressionSentRef.current = true
      sendAnalyticsEvent(`${DASHBOARD_TYPE}.view`, 'SIDEBAR_IMPRESSION')
    }
  }, [sendAnalyticsEvent])

  const handleNavClick = useCallback(
    (id: string) => () => {
      sendAnalyticsEvent(`${DASHBOARD_TYPE}.click`, 'SIDEBAR_LINK', {id})
    },
    [sendAnalyticsEvent],
  )

  const linksWithPaths = useMemo(
    () =>
      repoSidebarLinks.map(link => ({
        ...link,
        path: getRepoIssuesQueryPath({owner, repo, query: link.query}),
      })),
    [owner, repo],
  )

  const feedbackUrl = useFeedbackLink()

  // ── Legacy sidebar (flag OFF) ──────────────────────────────────────
  if (!isCollapsible) {
    return (
      <LegacySidebar
        linksWithPaths={linksWithPaths}
        selectedLink={selectedLink}
        issuesPath={issuesPath}
        projectsPath={projectsPath}
        viewsPath={viewsPath}
        milestonesPath={milestonesPath}
        milestonePath={milestonePath}
        labelsPath={labelsPath}
        pathname={pathname}
        forceHardNavigation={forceHardNavigation}
        handleNavClick={handleNavClick}
        feedbackUrl={feedbackUrl}
        hasProjectsEnabled={hasProjectsEnabled}
      />
    )
  }

  // ── Collapsible sidebar (flag ON) ──────────────────────────────────
  const viewsEnabled = isFeatureEnabled('issue_pinned_views_index_page')

  const pageItems: PageNavItemConfig[] = [
    ...(hasProjectsEnabled
      ? [
          {
            id: 'projects',
            path: projectsPath,
            icon: TableIcon,
            label: 'Projects',
            forceHardNavigation: !isFeatureEnabled('issue_pinned_views_projects_react'),
            isCurrent: pathname === projectsPath,
          } as const,
        ]
      : []),
    ...(viewsEnabled
      ? [
          {
            id: 'views',
            path: viewsPath,
            icon: StackIcon,
            label: 'Views',
            forceHardNavigation,
            isCurrent: pathname === viewsPath,
          } as const,
        ]
      : []),
    {
      id: 'milestones',
      path: milestonesPath,
      icon: MilestoneIcon,
      label: 'Milestones',
      forceHardNavigation,
      isCurrent: pathname.startsWith(milestonesPath) || pathname.startsWith(milestonePath),
    },
    {
      id: 'labels',
      path: labelsPath,
      icon: TagIcon,
      label: 'Labels',
      forceHardNavigation,
      isCurrent: pathname === labelsPath || pathname === `${issuesPath}/labels`,
    },
  ]

  return (
    <aside
      aria-label="Repository navigation"
      className={clsx(styles.sidebar, styles.sidebarCollapsible, !isExpanded && styles.sidebarCondensed)}
    >
      <SidebarList
        aria-label="Issue filters"
        dashboardType={DASHBOARD_TYPE}
        links={linksWithPaths}
        selectedLink={selectedLink}
        onSelectLink={noop}
        generateLinkPath={path => path ?? issuesPath}
        forceHardNavigation={forceHardNavigation}
        ItemWrapper={CollapsibleFilterItemWrapper}
      />

      <hr className={styles.divider} />

      <NavList aria-label="Pages">
        {pageItems.map(item => (
          <PageNavItem key={item.id} item={item} onClick={handleNavClick(item.id)} />
        ))}
      </NavList>
    </aside>
  )
}

/**
 * ── Legacy sidebar (flag OFF) ──────────────────────────────────────────
 *
 * Renders the exact same JSX as the `main` branch so the flag-OFF path
 * is visually identical. Uses `SidebarNavItem` + `BetaLabel`.
 */
function LegacySidebar({
  linksWithPaths,
  selectedLink,
  issuesPath,
  projectsPath,
  viewsPath,
  milestonesPath,
  milestonePath,
  labelsPath,
  pathname,
  forceHardNavigation,
  handleNavClick,
  feedbackUrl,
  hasProjectsEnabled,
}: {
  linksWithPaths: Array<SidebarLink & {path: string}>
  selectedLink: string
  issuesPath: string
  projectsPath: string
  viewsPath: string
  milestonesPath: string
  milestonePath: string
  labelsPath: string
  pathname: string
  forceHardNavigation: boolean
  handleNavClick: (id: string) => () => void
  feedbackUrl: string
  hasProjectsEnabled: boolean
}) {
  return (
    <aside aria-label="Repository navigation" className={styles.sidebar}>
      <SidebarList
        aria-label="Issue filters"
        dashboardType={DASHBOARD_TYPE}
        links={linksWithPaths}
        selectedLink={selectedLink}
        onSelectLink={noop}
        generateLinkPath={path => path ?? issuesPath}
        forceHardNavigation={forceHardNavigation}
      />

      <hr className={styles.divider} />

      <NavList aria-label="Pages">
        {hasProjectsEnabled && (
          <SidebarNavItem
            aria-current={pathname === projectsPath && 'page'}
            path={projectsPath}
            forceHardNavigation={!isFeatureEnabled('issue_pinned_views_projects_react')}
            icon={TableIcon}
            onClick={handleNavClick('projects')}
          >
            Projects
          </SidebarNavItem>
        )}
        {isFeatureEnabled('issue_pinned_views_index_page') && (
          <SidebarNavItem
            aria-current={pathname === viewsPath && 'page'}
            path={viewsPath}
            forceHardNavigation={forceHardNavigation}
            icon={StackIcon}
            onClick={handleNavClick('views')}
          >
            Views
          </SidebarNavItem>
        )}
        <SidebarNavItem
          aria-current={(pathname.startsWith(milestonesPath) || pathname.startsWith(milestonePath)) && 'page'}
          path={milestonesPath}
          forceHardNavigation={forceHardNavigation}
          icon={MilestoneIcon}
          onClick={handleNavClick('milestones')}
        >
          Milestones
        </SidebarNavItem>
        <SidebarNavItem
          aria-current={(pathname === labelsPath || pathname === `${issuesPath}/labels`) && 'page'}
          path={labelsPath}
          forceHardNavigation={forceHardNavigation}
          icon={TagIcon}
          onClick={handleNavClick('labels')}
        >
          Labels
        </SidebarNavItem>
      </NavList>

      <BetaLabel className={styles.betaLabel} feedbackUrl={feedbackUrl} />
    </aside>
  )
}

/**
 * NavList.Item wrapper used by the legacy (flag-OFF) sidebar. Uses polymorphic
 * `as` prop internally. NavList.LeadingVisual must be a direct child of
 * NavList.Item (enforced by primer-react/direct-slot-children lint rule), so
 * JSX is repeated in each branch.
 */
function SidebarNavItem({
  path,
  forceHardNavigation,
  'aria-current': ariaCurrent,
  icon: Icon,
  onClick,
  children,
}: {
  path: string
  forceHardNavigation: boolean
  'aria-current': false | 'page'
  icon: ComponentType
  onClick?: () => void
  children: ReactNode
}) {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      onClick?.()
      if (forceHardNavigation) {
        e.preventDefault()
        softNavigate(path)
      }
    },
    [path, onClick, forceHardNavigation],
  )

  if (forceHardNavigation) {
    return (
      <NavList.Item aria-current={ariaCurrent} href={path} onClick={handleClick}>
        <NavList.LeadingVisual>
          <Icon />
        </NavList.LeadingVisual>
        {children}
      </NavList.Item>
    )
  }
  return (
    <NavList.Item aria-current={ariaCurrent} as={Link} to={path} onClick={handleClick}>
      <NavList.LeadingVisual>
        <Icon />
      </NavList.LeadingVisual>
      {children}
    </NavList.Item>
  )
}

/*
 * ── Collapsible sidebar helpers (flag ON) ─────────────────────────────
 */

/**
 * Renders a Primer Tooltip around `children` when `enabled` is true,
 * otherwise renders children directly. Used to show the label on hover
 * in condensed mode without changing the tree structure.
 */
function ConditionalTooltip({text, enabled, children}: {text: string; enabled: boolean; children: ReactElement}) {
  return (
    <Tooltip text={text} direction="e" _privateDisableTooltip={!enabled}>
      {children}
    </Tooltip>
  )
}

/**
 * ItemWrapper for SidebarList that shows a tooltip on hover/focus in
 * condensed mode. Reads expanded state from context so it can be a
 * stable top-level component reference.
 */
function CollapsibleFilterItemWrapper({linkItem, children}: {linkItem: {name: string}; children: ReactElement}) {
  const {isExpanded} = useCollapsibleSidebarContext()
  return (
    <ConditionalTooltip text={linkItem.name} enabled={!isExpanded}>
      {children}
    </ConditionalTooltip>
  )
}

type PageNavItemConfig = {
  id: string
  path: string
  icon: ComponentType<{size?: number; className?: string}>
  label: string
  forceHardNavigation: boolean
  isCurrent: boolean
}

/**
 * Single NavList.Item used in both expanded and condensed modes. In condensed
 * mode the text label is visually hidden via CSS on `.sidebarCondensed` and a
 * Tooltip shows the label on hover/focus.
 *
 * NavList.LeadingVisual must be a direct JSX child of NavList.Item (enforced
 * by `primer-react/direct-slot-children`), so JSX is repeated in the
 * hard-nav vs soft-nav branches.
 */
function PageNavItem({item, onClick}: {item: PageNavItemConfig; onClick: () => void}) {
  const {isExpanded} = useCollapsibleSidebarContext()
  const {path, icon: Icon, label, forceHardNavigation, isCurrent} = item
  const ariaCurrent: 'page' | false = isCurrent ? 'page' : false
  const handleClick = useCallback(
    (e: MouseEvent) => {
      onClick()
      if (forceHardNavigation) {
        e.preventDefault()
        softNavigate(path)
      }
    },
    [path, onClick, forceHardNavigation],
  )

  if (forceHardNavigation) {
    return (
      <ConditionalTooltip text={label} enabled={!isExpanded}>
        <NavList.Item aria-current={ariaCurrent} href={path} onClick={handleClick}>
          <NavList.LeadingVisual>
            <Icon />
          </NavList.LeadingVisual>
          {label}
        </NavList.Item>
      </ConditionalTooltip>
    )
  }
  return (
    <ConditionalTooltip text={label} enabled={!isExpanded}>
      <NavList.Item aria-current={ariaCurrent} as={Link} to={path} onClick={handleClick}>
        <NavList.LeadingVisual>
          <Icon />
        </NavList.LeadingVisual>
        {label}
      </NavList.Item>
    </ConditionalTooltip>
  )
}
